import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownUp, ArrowRight, ArrowUpRight, CircleAlert, Contact, Crown, LayoutGrid, Network, RefreshCw, Rows3, Search, ShieldCheck, Trophy, User, UserRoundX, Users, X } from "lucide-react";

import { AuthContext } from "src/context/AuthContext";
import { WsDropdown, WsSegmented } from "src/stage/WsControls";
import { Skeleton, formatNumber } from "src/stage/StageUi";
import { characterFor } from "src/stage/characters";
import { combineInsights, fetchWorkspaceInsights } from "src/features/Stage/crmInsights";
import { useWorkspace } from "../context/WorkspaceContext";
import { getErrorMessage, getWorkspaceOverview } from "../services/workspaceService";
import { getWorkspaceUsers } from "../utils/teamMembersApi";
import { formatJoinedDate, normalizeWorkspaceUsers } from "../utils/teamMembersUtils";
import { TargetBar } from "src/features/Stage/targets/TargetCard";
import { listTargets } from "src/features/Stage/targets/targetsApi";
import { PERIODS, STATUS_RANK, formatValue, targetTitle } from "src/features/Stage/targets/targetMeta";
import "src/features/Stage/targets/targets.css";

// AUTO-CRM-32: the team as people, not a table. Who is on the team, who they
// report to, and what they got done in the period (from the CRM analytics).
const ROLE = {
  admin: { label: "Admin", icon: Crown, order: 0 },
  manager: { label: "Manager", icon: ShieldCheck, order: 1 },
  member: { label: "Member", icon: User, order: 2 },
};
const ACCESS = { active: "Active", inactive: "Inactive", pending: "Invited", invited: "Invited" };
// "invited" and "pending" both mean the person hasn't joined yet.
const accessOf = (status) => (status === "invited" ? "pending" : status || "inactive");
const LAYOUT_KEY = "crm.team.layout";
// Brand palette only: every avatar is a blue, sky, mint or navy gradient.
const AVATAR_TONES = [["#62B4E0", "#147DB5"], ["#147DB5", "#102F45"], ["#3FBF94", "#1B7A5A"], ["#8FCBEA", "#3A9BD3"], ["#1F86C2", "#0F4F77"], ["#2FA984", "#147DB5"]];

function initials(name = "") {
  return name.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "?";
}

export function PersonAvatar({ name, size = 40, status }) {
  let hash = 0;
  for (const char of String(name)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  const [from, to] = AVATAR_TONES[hash % AVATAR_TONES.length];
  return (
    <span className="crm-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: `linear-gradient(135deg, ${from}, ${to})` }} aria-hidden="true">
      {initials(name)}
      {status && <i className={`crm-avatar-dot is-${status}`} />}
    </span>
  );
}

function RoleBadge({ role }) {
  const config = ROLE[role] || ROLE.member;
  return <span className={`crm-role is-${role || "member"}`}><config.icon size={12} aria-hidden="true" />{config.label}</span>;
}

function Ring({ value, size = 44 }) {
  if (value === null || value === undefined) return <span className="crm-ring is-empty" style={{ width: size, height: size }} title="No activity in this period"><b>—</b></span>;
  const tone = value >= 80 ? "#2FA984" : value >= 50 ? "#147DB5" : "#C98A2E";
  return (
    <span className="crm-ring" style={{ width: size, height: size, background: `conic-gradient(${tone} ${value * 3.6}deg, rgba(16, 47, 69, .08) 0)` }} role="img" aria-label={`${value}% of planned work done`}>
      <b>{value}%</b>
    </span>
  );
}

const byRole = (a, b) => (ROLE[a.role]?.order ?? 3) - (ROLE[b.role]?.order ?? 3) || a.name.localeCompare(b.name);
const SORTS = {
  active: (a, b) => (b.stats?.completed_activities ?? -1) - (a.stats?.completed_activities ?? -1) || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
  role: byRole,
  newest: (a, b) => Date.parse(b.createdAt || 0) - Date.parse(a.createdAt || 0),
};

function Leaderboard({ loading, people, days, onView }) {
  const atlas = characterFor("atlas");
  const ranked = people.filter((person) => person.stats?.completed_activities > 0).sort(SORTS.active).slice(0, 3);
  const top = ranked[0]?.stats?.completed_activities || 1;
  return (
    <section className="ws-spot crm-team-spot ws-rise" aria-label="Leaderboard">
      <div className="ws-spot-art crm-home-spot-halo" aria-hidden="true" />
      {atlas?.full && <img className="ws-spot-art crm-team-spot-art" src={atlas.full} alt="" aria-hidden="true" />}
      <span className="ws-eyebrow"><Trophy size={12} aria-hidden="true" />Leaderboard · last {days} days</span>
      {loading ? <div className="crm-team-spot-list">{[0, 1, 2].map((key) => <Skeleton key={key} height={40} radius={14} style={{ opacity: 0.25 }} />)}</div>
        : ranked.length === 0 ? (
          <>
            <h2 className="crm-team-spot-title">No calls or follow-ups logged yet.</h2>
            <p className="ws-muted crm-team-spot-text">When your team logs calls, messages and meetings on leads, the most active people show up here.</p>
          </>
        ) : (
          <>
            <h2 className="crm-team-spot-title">{ranked[0].name.split(/\s+/)[0]} is leading the team.</h2>
            <ol className="crm-team-spot-list">
              {ranked.map((person, index) => (
                <li key={person.userId}>
                  <button type="button" onClick={() => onView(person)}>
                    <span className="crm-team-rank">{index + 1}</span>
                    <PersonAvatar name={person.name} size={32} />
                    <span className="crm-team-spot-who"><b>{person.name}</b><small>{formatNumber(person.stats.completed_activities)} done · {formatNumber(person.stats.leads_touched ?? person.stats.assigned_leads)} leads worked</small></span>
                    <span className="crm-team-spot-bar" aria-hidden="true"><i style={{ width: `${Math.max(8, (person.stats.completed_activities / top) * 100)}%` }} /></span>
                  </button>
                </li>
              ))}
            </ol>
          </>
        )}
    </section>
  );
}

function Stat({ label, value, detail, warn, meter, icon: Icon }) {
  return (
    <div className={`crm-team-stat${warn ? " is-warn" : ""}`}>
      <span className="crm-team-stat-icon" aria-hidden="true"><Icon size={16} /></span>
      <span className="crm-team-stat-label">{label}</span>
      <span className="crm-team-stat-value">{value}</span>
      {meter !== undefined && <span className="ws-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={meter} aria-label={label}><span style={{ width: `${meter}%` }} /></span>}
      <span className="crm-team-stat-detail">{detail}</span>
    </div>
  );
}

function PersonCard({ person, directs, isSelf, onView, targets = [] }) {
  const stats = person.stats;
  const line = person.reportsTo
    ? <><PersonAvatar name={person.reportsTo} size={20} /><span>Reports to <b>{person.reportsTo}</b></span></>
    : person.role === "member" ? <span className="crm-person-warn"><UserRoundX size={13} aria-hidden="true" />No manager yet</span>
      : directs ? <><Users size={13} aria-hidden="true" /><span>Leads a team of <b>{directs}</b></span></>
        : <><Crown size={13} aria-hidden="true" /><span>{person.role === "admin" ? "Runs the workspace" : "No one reports to them yet"}</span></>;
  return (
    <li className={`crm-person${person.status !== "active" ? " is-muted" : ""}`}>
      <div className="crm-person-top">
        <PersonAvatar name={person.name} size={48} status={person.status} />
        <div className="crm-person-id">
          <button type="button" className="crm-person-name" onClick={() => onView(person)}>{person.name}</button>
          {isSelf && <span className="crm-you">You</span>}
          <span className="crm-person-mail" title={person.email}>{person.email}</span>
        </div>
        <RoleBadge role={person.role} />
      </div>
      <div className="crm-person-line">{line}</div>
      <div className="crm-person-stats">
        <div><b>{stats ? formatNumber(stats.assigned_leads) : "—"}</b><span>Leads owned</span></div>
        <div><b>{stats ? formatNumber(stats.completed_activities) : "—"}</b><span>Done</span></div>
        <div><b>{stats ? formatNumber(stats.leads_touched ?? 0) : "—"}</b><span>Leads worked</span></div>
        <Ring value={stats?.activities ? stats.completion_rate : null} />
      </div>
      {targets.length > 0 && (() => {
        const target = targets[0];
        return (
          <button type="button" className="crm-person-target" onClick={() => onView(person, "targets")} aria-label={`${person.name}'s target: ${targetTitle(target)}`}>
            <span className="crm-person-target-top"><span>Target · {targetTitle(target)} {PERIODS[target.period]?.this}{targets.length > 1 ? ` · +${targets.length - 1} more` : ""}</span><b>{formatValue(target, target.progress?.actual)} / {formatValue(target, target.target_value)}</b></span>
            <TargetBar target={target} size="sm" />
          </button>
        );
      })()}
      <footer className="crm-person-foot">
        <span className={`crm-access is-${person.status}`}><i aria-hidden="true" />{ACCESS[person.status] || person.status}</span>
        <span className="crm-person-joined">Joined {formatJoinedDate(person.createdAt)}</span>
        <button type="button" className="crm-person-open" onClick={() => onView(person)} aria-label={`View performance of ${person.name}`}>Performance<ArrowRight size={13} /></button>
      </footer>
    </li>
  );
}

function OrgView({ people, currentUserId, onView, organisationUrl }) {
  const byId = new Map(people.map((person) => [String(person.userId), person]));
  const reports = new Map();
  people.forEach((person) => {
    if (person.reportsToUserId && byId.has(String(person.reportsToUserId))) {
      const key = String(person.reportsToUserId);
      reports.set(key, [...(reports.get(key) || []), person]);
    }
  });
  const leads = people.filter((person) => reports.has(String(person.userId)) || person.role === "manager").sort(byRole);
  const admins = people.filter((person) => person.role === "admin");
  const loose = people.filter((person) => person.role === "member" && !(person.reportsToUserId && byId.has(String(person.reportsToUserId))));
  const teamRate = (members) => {
    const planned = members.reduce((total, member) => total + (member.stats?.activities || 0), 0);
    const done = members.reduce((total, member) => total + (member.stats?.completed_activities || 0), 0);
    return planned ? Math.round((done / planned) * 100) : null;
  };
  return (
    <div className="crm-org">
      <section className="crm-org-admins" aria-label="Workspace admins">
        <span className="crm-org-label"><Crown size={13} aria-hidden="true" />Workspace admins</span>
        <div className="crm-org-chips">
          {admins.map((person) => (
            <button key={person.userId} type="button" className="crm-org-chip" onClick={() => onView(person)}>
              <PersonAvatar name={person.name} size={26} status={person.status} /><span>{person.name}{String(person.userId) === String(currentUserId) ? " (you)" : ""}</span>
            </button>
          ))}
        </div>
      </section>
      <div className="crm-org-teams">
        {leads.map((lead) => {
          const members = (reports.get(String(lead.userId)) || []).sort(SORTS.active);
          const rate = teamRate(members);
          return (
            <section key={lead.userId} className="crm-org-team" aria-label={`${lead.name}'s team`}>
              <header className="crm-org-team-head">
                <PersonAvatar name={lead.name} size={40} status={lead.status} />
                <div className="ws-grow">
                  <button type="button" className="crm-person-name" onClick={() => onView(lead)}>{lead.name}</button>
                  <span className="crm-org-team-sub"><RoleBadge role={lead.role} />{members.length} {members.length === 1 ? "person" : "people"}</span>
                </div>
                <Ring value={rate} size={40} />
              </header>
              {members.length ? (
                <ul className="crm-org-members">
                  {members.map((member) => (
                    <li key={member.userId}>
                      <button type="button" onClick={() => onView(member)}>
                        <PersonAvatar name={member.name} size={28} status={member.status} />
                        <span className="crm-org-member-name">{member.name}</span>
                        <span className="crm-org-member-stat">{member.stats ? `${formatNumber(member.stats.assigned_leads)} leads · ${formatNumber(member.stats.completed_activities)} done` : "—"}</span>
                        <span className="crm-org-member-bar" aria-hidden="true"><i style={{ width: `${member.stats?.activities ? member.stats.completion_rate : 0}%` }} /></span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className="crm-org-empty">No one reports to {lead.name.split(/\s+/)[0]} yet.</p>}
            </section>
          );
        })}
        {leads.length === 0 && <p className="crm-org-empty">No managers yet. Give members a manager in Roles and access so someone reviews their follow-ups.</p>}
      </div>
      {loose.length > 0 && (
        <section className="crm-org-loose" aria-label="Not in a team yet">
          <div className="ws-grow">
            <span className="crm-org-label is-warn"><CircleAlert size={13} aria-hidden="true" />Not in a team yet · {loose.length}</span>
            <p>These members don’t report to anyone, so nobody reviews their follow-ups.</p>
            <div className="crm-org-chips">
              {loose.map((person) => <button key={person.userId} type="button" className="crm-org-chip" onClick={() => onView(person)}><PersonAvatar name={person.name} size={26} status={person.status} /><span>{person.name}</span></button>)}
            </div>
          </div>
          <a className="ws-btn ws-btn--quiet ws-btn--sm" href={organisationUrl}>Set managers<ArrowUpRight size={13} /></a>
        </section>
      )}
    </div>
  );
}

function PeopleList({ people, currentUserId, onView }) {
  return (
    <div className="crm-people-list" role="table" aria-label="Team members">
      <div className="crm-people-row is-head" role="row">
        <span role="columnheader">Member</span><span role="columnheader">Role</span><span role="columnheader">Reports to</span>
        <span role="columnheader" className="is-num">Leads</span><span role="columnheader" className="is-num">Done</span><span role="columnheader">Completion</span>
        <span role="columnheader">Access</span><span role="columnheader">Joined</span><span role="columnheader" aria-label="Open" />
      </div>
      {people.map((person) => (
        <div key={person.userId} className="crm-people-row" role="row">
          <span role="cell" className="crm-people-who"><PersonAvatar name={person.name} size={34} status={person.status} /><span><button type="button" className="crm-person-name" onClick={() => onView(person)}>{person.name}</button>{String(person.userId) === String(currentUserId) && <span className="crm-you">You</span>}<small>{person.email}</small></span></span>
          <span role="cell"><RoleBadge role={person.role} /></span>
          <span role="cell" className="crm-people-mgr">{person.reportsTo ? <><PersonAvatar name={person.reportsTo} size={22} />{person.reportsTo}</> : <span className={person.role === "member" ? "crm-person-warn" : "ws-muted"}>{person.role === "member" ? "No manager" : "—"}</span>}</span>
          <span role="cell" className="is-num">{person.stats ? formatNumber(person.stats.assigned_leads) : "—"}</span>
          <span role="cell" className="is-num">{person.stats ? formatNumber(person.stats.completed_activities) : "—"}</span>
          <span role="cell" className="crm-people-rate">{person.stats?.activities ? <><span className="ws-meter"><span style={{ width: `${person.stats.completion_rate}%` }} /></span><b>{person.stats.completion_rate}%</b></> : <span className="ws-muted">—</span>}</span>
          <span role="cell"><span className={`crm-access is-${person.status}`}><i aria-hidden="true" />{ACCESS[person.status] || person.status}</span></span>
          <span role="cell" className="ws-muted">{formatJoinedDate(person.createdAt)}</span>
          <span role="cell"><button type="button" className="crm-icon-btn crm-icon-btn--sm" onClick={() => onView(person)} aria-label={`View performance of ${person.name}`} title="Performance"><ArrowRight size={14} /></button></span>
        </div>
      ))}
    </div>
  );
}

export default function TeamDirectory({ organisationUrl }) {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { currentWorkspaceId } = useWorkspace();
  const [users, setUsers] = useState(null);
  const [insights, setInsights] = useState(undefined);
  const [targets, setTargets] = useState([]);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [days, setDays] = useState("30");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [access, setAccess] = useState("all");
  const [sort, setSort] = useState("active");
  const [layout, setLayoutState] = useState(() => { try { return localStorage.getItem(LAYOUT_KEY) || "cards"; } catch { return "cards"; } });
  const setLayout = (value) => { setLayoutState(value); try { localStorage.setItem(LAYOUT_KEY, value); } catch { /* private mode */ } };

  const loadPeople = useCallback(async () => {
    if (!currentWorkspaceId) return;
    setError("");
    try {
      const [rows, summary] = await Promise.all([getWorkspaceUsers(currentWorkspaceId), getWorkspaceOverview(currentWorkspaceId).catch(() => null)]);
      setUsers(normalizeWorkspaceUsers(rows));
      setOverview(summary);
      if (!summary) setInsights(null);
    } catch (loadError) {
      setUsers([]);
      setError(getErrorMessage(loadError, "Unable to load team members."));
    }
  }, [currentWorkspaceId]);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => {
    let active = true;
    listTargets().then((result) => { if (active) setTargets(Array.isArray(result?.data) ? result.data : []); }).catch(() => {});
    return () => { active = false; };
  }, [currentWorkspaceId]);
  // Personal targets per person, the one needing most attention first.
  const targetsByPerson = useMemo(() => {
    const map = new Map();
    targets.filter((target) => target.user?.id).forEach((target) => map.set(String(target.user.id), [...(map.get(String(target.user.id)) || []), target]));
    map.forEach((list) => list.sort((a, b) => (STATUS_RANK[a.progress?.status] ?? 2) - (STATUS_RANK[b.progress?.status] ?? 2)));
    return map;
  }, [targets]);

  // Per-person numbers come from each pipeline's analytics; a failed pipeline
  // just leaves its numbers out.
  useEffect(() => {
    if (!overview) return undefined;
    let active = true;
    setInsights(undefined);
    const pipelines = (overview.pipelines || []).filter((pipeline) => pipeline.status !== "archived");
    fetchWorkspaceInsights(pipelines, { days: Number(days) })
      .then((rows) => { if (active) setInsights(combineInsights(rows)); })
      .catch(() => { if (active) setInsights(null); });
    return () => { active = false; };
  }, [overview, days]);

  const people = useMemo(() => {
    const stats = new Map((insights?.team || []).map((row) => [String(row.user?.id), row]));
    return (users || []).map((user) => ({ ...user, status: accessOf(user.status), stats: stats.get(String(user.userId)) || null }));
  }, [users, insights]);
  const directs = useMemo(() => {
    const counts = new Map();
    people.forEach((person) => { if (person.reportsToUserId) counts.set(String(person.reportsToUserId), (counts.get(String(person.reportsToUserId)) || 0) + 1); });
    return counts;
  }, [people]);
  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    return people.filter((person) => (!query || [person.name, person.email, person.reportsTo, ROLE[person.role]?.label].filter(Boolean).join(" ").toLowerCase().includes(query))
      && (role === "all" || person.role === role) && (access === "all" || person.status === access)).sort(SORTS[sort]);
  }, [people, search, role, access, sort]);

  const loading = users === null;
  const statsLoading = insights === undefined && !error;
  const view = (person, section) => navigate(section === "targets" ? "/targets" : `/teams/${person.userId}`);
  const count = (predicate) => people.filter(predicate).length;
  const planned = people.reduce((total, person) => total + (person.stats?.activities || 0), 0);
  const done = people.reduce((total, person) => total + (person.stats?.completed_activities || 0), 0);
  const owned = people.reduce((total, person) => total + (person.stats?.assigned_leads || 0), 0);
  const loose = count((person) => person.role === "member" && !person.reportsToUserId);
  const unowned = overview?.stats?.leads?.unassigned;
  const filtered = Boolean(search || role !== "all" || access !== "all");

  return (
    <div className="crm-team-body">
      {error && <div className="ws-notice" role="alert"><CircleAlert size={16} style={{ flex: "none", marginTop: 1 }} /><span className="ws-grow">{error} Your team settings were not changed.</span><button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={loadPeople}><RefreshCw size={13} />Try again</button></div>}

      <div className="crm-team-top">
        <Leaderboard loading={loading || statsLoading} people={people} days={days} onView={view} />
        <section className="ws-card crm-team-stats ws-rise" aria-label="Team summary">
          <header className="crm-team-stats-head">
            <h2 className="ws-h3 ws-grow">Team at a glance</h2>
            <WsSegmented label="Period" value={days} onChange={setDays} items={[{ id: "7", label: "7d" }, { id: "30", label: "30d" }, { id: "90", label: "90d" }]} />
          </header>
          <div className="crm-team-stat-grid">
            <Stat icon={Users} label="People" value={loading ? "—" : formatNumber(people.length)} detail={loading ? "Loading…" : `${count((person) => person.status === "active")} active${count((person) => person.status === "pending") ? ` · ${count((person) => person.status === "pending")} invited` : ""}`} />
            <Stat icon={Contact} label="Leads owned" value={statsLoading ? "—" : formatNumber(owned)} detail={unowned ? `${formatNumber(unowned)} leads have no owner yet` : "Every lead has an owner"} warn={Boolean(unowned)} />
            <Stat icon={Trophy} label="Work done" value={statsLoading ? "—" : `${formatNumber(done)}`} detail={planned ? `${Math.round((done / planned) * 100)}% of ${formatNumber(planned)} planned` : "Nothing planned in this period"} meter={planned ? Math.round((done / planned) * 100) : undefined} />
            <Stat icon={Network} label="Reporting lines" value={loading ? "—" : formatNumber(count((person) => person.role === "manager"))} detail={loose ? `${loose} ${loose === 1 ? "member has" : "members have"} no manager` : "Every member has a manager"} warn={loose > 0} />
          </div>
        </section>
      </div>

      <section className="ws-card crm-team-card" aria-label="People">
        <div className="crm-team-toolbar">
          <div className="ws-controls">
            <label className="ws-searchbox crm-team-search"><Search size={15} aria-hidden="true" /><input type="search" aria-label="Search team members" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email or manager" />{search && <button type="button" className="crm-doc-clear" aria-label="Clear search" onClick={() => setSearch("")}><X size={13} /></button>}</label>
            {layout !== "org" && <WsDropdown label="Role" icon={ShieldCheck} value={role} onChange={setRole} options={[{ value: "all", label: "All roles", short: "All" }, { value: "admin", label: "Admins" }, { value: "manager", label: "Managers" }, { value: "member", label: "Members" }]} />}
            {layout !== "org" && <WsDropdown label="Access" icon={User} value={access} onChange={setAccess} options={[{ value: "all", label: "Everyone", short: "All" }, { value: "active", label: "Active" }, { value: "pending", label: "Invited" }, { value: "inactive", label: "Inactive" }]} />}
            {layout !== "org" && <WsDropdown label="Sort" icon={ArrowDownUp} align="right" value={sort} onChange={setSort} options={[{ value: "active", label: "Most work done" }, { value: "name", label: "Name A–Z" }, { value: "role", label: "Role" }, { value: "newest", label: "Newest first" }]} />}
            <span className="crm-team-layout"><WsSegmented label="Layout" value={layout} onChange={setLayout} items={[{ id: "cards", icon: LayoutGrid, label: "People" }, { id: "org", icon: Network, label: "Teams" }, { id: "list", icon: Rows3, label: "List" }]} /></span>
          </div>
          {filtered && layout !== "org" && <div className="crm-doc-active"><span>{shown.length} of {people.length} people</span><button type="button" className="crm-link-btn" onClick={() => { setSearch(""); setRole("all"); setAccess("all"); }}>Clear filters</button></div>}
        </div>

        {loading ? (
          <ul className="crm-person-grid">{[0, 1, 2].map((key) => <li key={key}><Skeleton height={236} radius={18} /></li>)}</ul>
        ) : shown.length === 0 ? (
          <div className="crm-doc-empty"><span className="crm-team-empty-art" aria-hidden="true"><Users size={24} /></span><h3 className="ws-h3">{filtered ? "No one matches" : "No team members yet"}</h3><p className="ws-muted">{filtered ? "Try another name, role or access." : "Invite people in Auto Appy to share the leads."}</p></div>
        ) : layout === "org" ? (
          <OrgView people={people.filter((person) => !search || shown.includes(person))} currentUserId={currentUser?.id} onView={view} organisationUrl={organisationUrl} />
        ) : layout === "list" ? (
          <PeopleList people={shown} currentUserId={currentUser?.id} onView={view} />
        ) : (
          <ul className="crm-person-grid">{shown.map((person) => <PersonCard key={person.userId} person={person} directs={directs.get(String(person.userId)) || 0} isSelf={String(person.userId) === String(currentUser?.id)} onView={view} targets={targetsByPerson.get(String(person.userId)) || []} />)}</ul>
        )}
        {!loading && insights === null && <p className="crm-team-note"><CircleAlert size={13} aria-hidden="true" />Activity numbers are unavailable right now; people and roles are up to date.</p>}
      </section>
    </div>
  );
}
